import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ProductCard from "../components/ProductCard";

const ScrollToHash = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);
  return null;
};

export const Home = () => {
  return (
    <>
      <ScrollToHash />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="af-hero-glow af-grain"
        style={{ position: "relative", overflow: "hidden" }}
      >
        <div className="af-container" style={{ paddingTop: "6.5rem", paddingBottom: "5.5rem", position: "relative" }}>
          <div className="af-eyebrow" data-testid="hero-eyebrow">Canadian software company</div>

          <h1 className="af-hero-h1" style={{ marginTop: 18 }} data-testid="hero-company-name">
            AithorityForge Solutions Ltd.
          </h1>

          <p
            className="af-hero-h1"
            data-testid="hero-headline"
            style={{
              marginTop: 22,
              fontSize: "clamp(1.45rem, 2.6vw, 2.05rem)",
              fontWeight: 500,
              color: "#cfd3dc",
              lineHeight: 1.3,
              maxWidth: 720,
            }}
          >
            Simple AI tools for real-world problems.
          </p>

          <div className="af-lede" style={{ marginTop: 24 }} data-testid="hero-body">
            <p style={{ marginBottom: 14 }}>
              AithorityForge Solutions Ltd. is a Canadian software company building practical AI products that help
              people organize information, turn work into systems, and make better use of what they already know.
            </p>
            <p>
              We build tools that are simple to understand, useful in real life, and careful about what they claim.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 mt-8" data-testid="hero-cta-group">
            <a href="#products" className="af-btn af-btn-primary" data-testid="hero-products-cta">
              See Our Products
            </a>
            <Link to="/contact" className="af-btn af-btn-ghost" data-testid="hero-contact-cta">
              Contact Us
            </Link>
          </div>

          <div
            data-testid="hero-location-line"
            style={{ marginTop: 22, color: "var(--af-text-muted)", fontSize: "0.88rem" }}
          >
            Built in Regina, Saskatchewan, Canada.
          </div>

          {/* Quiet abstract product-areas visual */}
          <div
            aria-hidden
            data-testid="hero-areas-visual"
            className="grid grid-cols-3 gap-3 md:gap-4"
            style={{ marginTop: 56, maxWidth: 720 }}
          >
            {[
              { k: "Work", color: "rgba(183, 65, 14, 0.5)" },
              { k: "Research", color: "rgba(27, 54, 93, 0.85)" },
              { k: "Reflection", color: "rgba(107, 70, 193, 0.45)" },
            ].map((it) => (
              <div
                key={it.k}
                style={{
                  border: "1px solid var(--af-border-strong)",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
                  borderRadius: 12,
                  padding: "1.2rem 1.1rem",
                  minHeight: 110,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: it.color,
                  }}
                />
                <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem", letterSpacing: "-0.01em" }}>
                  {it.k}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section className="af-section" data-testid="what-we-build-section">
        <div className="af-container">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="af-eyebrow">01 — What We Build</div>
              <h2 className="af-h2" style={{ marginTop: 14 }}>What We Build</h2>
            </div>
            <div className="md:col-span-7 af-lede">
              <p style={{ marginBottom: 14 }}>
                AithorityForge builds AI-powered products for everyday work, research, and reflection.
              </p>
              <p style={{ marginBottom: 14 }}>Our goal is simple:</p>
              <p style={{ color: "#fff", fontWeight: 600, fontSize: "1.15rem", marginBottom: 22 }}>
                Make AI useful, clear, and trustworthy.
              </p>

              <div className="grid gap-3 sm:grid-cols-3" data-testid="what-we-build-statements">
                {["Not hype.", "Not confusing dashboards.", "Not tools that pretend to know more than they do."].map(
                  (s) => (
                    <div
                      key={s}
                      style={{
                        border: "1px solid var(--af-border)",
                        background: "var(--af-surface)",
                        borderRadius: 10,
                        padding: "0.95rem 1rem",
                        color: "#cfd3dc",
                        fontSize: "0.92rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {s}
                    </div>
                  )
                )}
              </div>

              <p style={{ marginTop: 22, color: "#cfd3dc" }}>
                Just software that helps people do something real.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="af-container"><div className="af-divider" /></div>

      {/* PRODUCTS */}
      <section id="products" className="af-section" data-testid="products-section">
        <div className="af-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4" style={{ marginBottom: 36 }}>
            <div>
              <div className="af-eyebrow">02 — Products</div>
              <h2 className="af-h2" style={{ marginTop: 14 }}>Products</h2>
            </div>
            <p className="af-body" style={{ maxWidth: 380 }}>
              Three focused products. Each one solves a specific real-world problem.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3" data-testid="products-grid">
            <ProductCard
              testId="product-card-pin"
              name="PIN — Peptide Intelligence Network"
              shortName="PIN"
              description={`PIN helps people explore peptide information in a more organized way.

It brings peptide profiles, evidence levels, approval status, side effects, cost ranges, and comparisons into one place.`}
              disclaimer="Research intelligence only. Not medical advice. No dosing. No protocols."
              status="Preparing for launch"
              accent="copper"
            />
            <ProductCard
              testId="product-card-clip2sop"
              name="Clip2SOP"
              shortName="C2S"
              description={`Clip2SOP helps businesses turn real work into clear process.

Record yourself doing the work once. Clip2SOP turns it into the checklist, the training document, proof the work was done, and a reviewable history.`}
              status="In active build"
              accent="copper"
            />
            <ProductCard
              testId="product-card-soulscript"
              name="SoulScript"
              shortName="SS"
              description={`SoulScript is a scripture and prayer reflection app built around emotion, faith, and personal reflection.

It helps people slow down, reflect, pray, and organize meaningful thoughts.`}
              disclaimer="Not therapy. Not medical advice. Not a replacement for professionals."
              status="Testing and preparing for launch"
              accent="purple"
            />
          </div>
        </div>
      </section>

      <div className="af-container"><div className="af-divider" /></div>

      {/* ABOUT */}
      <section className="af-section" data-testid="about-section">
        <div className="af-container">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="af-eyebrow">03 — About</div>
              <h2 className="af-h2" style={{ marginTop: 14 }}>About AithorityForge</h2>
            </div>
            <div className="md:col-span-7 af-lede">
              <p style={{ marginBottom: 14 }}>
                AithorityForge Solutions Ltd. is based in Regina, Saskatchewan, Canada.
              </p>
              <p style={{ marginBottom: 14 }}>
                We are building a group of focused AI products under one company.
              </p>
              <p>
                Each product is designed to solve a real problem, avoid overpromising, and keep the user in control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT CAREFULLY */}
      <section className="af-section" data-testid="built-carefully-section" style={{ paddingTop: 0 }}>
        <div className="af-container">
          <div
            className="af-panel"
            style={{
              background: "linear-gradient(180deg, #15203A 0%, #101A30 100%)",
              borderColor: "rgba(234, 234, 234, 0.10)",
              padding: "clamp(2rem, 4vw, 3rem)",
            }}
          >
            <div className="grid gap-10 md:grid-cols-12">
              <div className="md:col-span-5">
                <div className="af-eyebrow">04 — How we work</div>
                <h2 className="af-h2" style={{ marginTop: 14 }}>Built Carefully</h2>
              </div>
              <div className="md:col-span-7">
                <p className="af-lede" style={{ marginBottom: 14 }}>
                  We do not believe AI should pretend to be perfect.
                </p>
                <p className="af-lede" style={{ marginBottom: 24 }}>
                  That is why our products are built with clear limits, simple language, and careful claims.
                </p>

                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }} data-testid="built-carefully-list">
                  {[
                    "When something is research-only, we say so.",
                    "When something is not medical advice, we say so.",
                    "When something needs human judgment, we keep the human in charge.",
                  ].map((line) => (
                    <li
                      key={line}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                        color: "#e7eaf0",
                        fontSize: "1rem",
                        lineHeight: 1.6,
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid var(--af-border)",
                        padding: "0.85rem 1rem",
                        borderRadius: 10,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          marginTop: 8,
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          background: "var(--af-copper)",
                          flexShrink: 0,
                        }}
                      />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="af-section" data-testid="contact-section" style={{ paddingTop: "3rem" }}>
        <div className="af-container">
          <div className="grid gap-10 md:grid-cols-12 items-start">
            <div className="md:col-span-5">
              <div className="af-eyebrow">05 — Contact</div>
              <h2 className="af-h2" style={{ marginTop: 14 }}>Contact</h2>
            </div>
            <div className="md:col-span-7 af-lede">
              <p style={{ marginBottom: 18 }}>
                Have a question, partnership idea, or want to follow what we are building?
              </p>
              <a
                href="mailto:stefan@aithorityforge.com"
                data-testid="home-contact-email"
                className="af-btn af-btn-primary"
                style={{ fontSize: "1rem" }}
              >
                stefan@aithorityforge.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
